/**
 * PointerTracker — core gesture coordinator for component islands.
 *
 * Single instance per island. Normalises Pointer Events API, tracks active
 * pointers, delegates to pluggable recognizers, caches dimensions.
 *
 * Returns a no-op instance on non-touch devices (navigator.maxTouchPoints === 0).
 */

import { SwipeRecognizer } from './swipe.js';
import { PanRecognizer } from './pan.js';
import { PinchRecognizer } from './pinch.js';
import { DismissRecognizer } from './dismiss.js';

const NOOP = {
    destroy() {},
    get isNoop() { return true; },
};

export class PointerTracker {
    constructor(element, config = {}) {
        if (typeof navigator !== 'undefined' && navigator.maxTouchPoints === 0) {
            return NOOP;
        }

        this.el = element;
        this.config = config;
        this.pointers = new Map();
        this.bounds = null;
        this.destroyed = false;
        this.recognizers = [];
        this._axisLock = null;
        this._singlePointerRecognizers = [];
        this._pinchRecognizer = null;
        this._pinchActive = false;

        this._onDown = this._onDown.bind(this);
        this._onMove = this._onMove.bind(this);
        this._onUp = this._onUp.bind(this);
        this._onCancel = this._onCancel.bind(this);

        this.el.addEventListener('pointerdown', this._onDown);
        this.el.addEventListener('pointermove', this._onMove);
        this.el.addEventListener('pointerup', this._onUp);
        this.el.addEventListener('pointercancel', this._onCancel);

        this._initRecognizers();
    }

    _initRecognizers() {
        const { config } = this;
        const callbacks = {
            onSwipe: config.onSwipe,
            onPanMove: config.onPanMove,
            onPanEnd: config.onPanEnd,
            onPinch: config.onPinch,
            onPinchEnd: config.onPinchEnd,
            onDismissMove: config.onDismissMove,
            onDismiss: config.onDismiss,
            onDismissCancel: config.onDismissCancel,
        };

        if (config.swipe) {
            const r = new SwipeRecognizer(config.swipe, callbacks);
            this.recognizers.push(r);
            this._singlePointerRecognizers.push(r);
        }

        if (config.pan) {
            const r = new PanRecognizer(config.pan, callbacks);
            this.recognizers.push(r);
            this._singlePointerRecognizers.push(r);
        }

        if (config.pinch) {
            const r = new PinchRecognizer(config.pinch, callbacks);
            this.recognizers.push(r);
            this._pinchRecognizer = r;
        }

        if (config.dismiss) {
            const r = new DismissRecognizer(config.dismiss, callbacks);
            this.recognizers.push(r);
            this._singlePointerRecognizers.push(r);
        }
    }

    _cacheBounds() {
        if (!this.bounds) {
            this.bounds = this.el.getBoundingClientRect();
        }
    }

    _createPointerState(e) {
        return {
            id: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            currentX: e.clientX,
            currentY: e.clientY,
            startTime: e.timeStamp,
            history: [{ x: e.clientX, y: e.clientY, t: e.timeStamp }],
        };
    }

    _updatePointerState(state, e) {
        state.currentX = e.clientX;
        state.currentY = e.clientY;
        state.history.push({ x: e.clientX, y: e.clientY, t: e.timeStamp });
        if (state.history.length > 5) {
            state.history.shift();
        }
    }

    _onDown(e) {
        if (this.destroyed) return;
        this._cacheBounds();
        this.pointers.set(e.pointerId, this._createPointerState(e));

        const count = this.pointers.size;

        if (count === 2 && this._pinchRecognizer) {
            this._pinchActive = true;
            this._singlePointerRecognizers.forEach(r => r.pause?.());
            const [p1, p2] = [...this.pointers.values()];
            this._pinchRecognizer.start(p1, p2);
        } else if (count === 1) {
            this._axisLock = null;
            this._singlePointerRecognizers.forEach(r => r.start?.(this.pointers.values().next().value, this.bounds));
        }
    }

    _onMove(e) {
        if (this.destroyed) return;
        const state = this.pointers.get(e.pointerId);
        if (!state) return;

        this._updatePointerState(state, e);

        if (this._pinchActive && this._pinchRecognizer && this.pointers.size >= 2) {
            const [p1, p2] = [...this.pointers.values()];
            this._pinchRecognizer.move(p1, p2);
            return;
        }

        // Axis lock: first 10px determines dominant axis
        if (!this._axisLock && this.pointers.size === 1) {
            const dx = Math.abs(state.currentX - state.startX);
            const dy = Math.abs(state.currentY - state.startY);
            if (dx > 10 || dy > 10) {
                this._axisLock = dx >= dy ? 'horizontal' : 'vertical';
            }
        }

        if (this.pointers.size === 1) {
            this._singlePointerRecognizers.forEach(r => r.move?.(state, this._axisLock));
        }
    }

    _onUp(e) {
        if (this.destroyed) return;
        const state = this.pointers.get(e.pointerId);
        if (!state) return;

        this._updatePointerState(state, e);

        if (this._pinchActive && this.pointers.size <= 2) {
            this._pinchRecognizer.end?.();
            this._pinchActive = false;
            this.pointers.delete(e.pointerId);

            // If one pointer remains, it may resume single-pointer recognizers
            if (this.pointers.size === 1) {
                const remaining = this.pointers.values().next().value;
                this._axisLock = null;
                this._singlePointerRecognizers.forEach(r => r.resume?.(remaining, this.bounds));
            }
            return;
        }

        if (this.pointers.size === 1) {
            this._singlePointerRecognizers.forEach(r => r.end?.(state, this._axisLock));
        }

        this.pointers.delete(e.pointerId);

        if (this.pointers.size === 0) {
            this.bounds = null;
            this._axisLock = null;
        }
    }

    _onCancel(e) {
        if (this.destroyed) return;
        const state = this.pointers.get(e.pointerId);

        if (this._pinchActive) {
            this._pinchRecognizer.cancel?.();
            this._pinchActive = false;
        } else if (state) {
            this._singlePointerRecognizers.forEach(r => r.cancel?.(state));
        }

        this.pointers.delete(e.pointerId);

        if (this.pointers.size === 0) {
            this.bounds = null;
            this._axisLock = null;
        }
    }

    destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        this.el.removeEventListener('pointerdown', this._onDown);
        this.el.removeEventListener('pointermove', this._onMove);
        this.el.removeEventListener('pointerup', this._onUp);
        this.el.removeEventListener('pointercancel', this._onCancel);
        this.recognizers.forEach(r => r.destroy?.());
        this.recognizers = [];
        this._singlePointerRecognizers = [];
        this._pinchRecognizer = null;
        this.pointers.clear();
        this.bounds = null;
    }

    get isNoop() { return false; }
}
